import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Clock, Menu, X, Globe, ChevronDown } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export default function MarketingLayout({ children, title, description, keywords }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const currentLang = i18n.language || 'nl';

  const navLinks = [
    { href: '/', label: t('marketing.nav.home') },
    { href: '/functies', label: t('marketing.nav.features') },
    { href: '/prijzen', label: t('marketing.nav.pricing') },
    { href: '/over-ons', label: t('marketing.nav.about') },
    { href: '/contact', label: t('marketing.nav.contact') },
    { href: '/blog', label: t('marketing.nav.blog') }
  ];

  const isActive = (path) => location.pathname === path;

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const defaultTitle = 'Urenbuddy - Tijd- en Kilometerregistratie voor ZZP\'ers';
  const defaultDescription = 'De complete oplossing voor Nederlandse ZZP\'ers en freelancers. Registreer uren, kilometers en maak professionele facturen. Gratis starten!';
  const defaultKeywords = 'urenregistratie, zzp, freelancer, kilometerregistratie, facturatie, tijdregistratie, administratie, btw aangifte';

  return (
    <>
      <Helmet>
        <title>{title || defaultTitle}</title>
        <meta name="description" content={description || defaultDescription} />
        <meta name="keywords" content={keywords || defaultKeywords} />
        <meta property="og:title" content={title || defaultTitle} />
        <meta property="og:description" content={description || defaultDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={currentLang === 'nl' ? 'nl_NL' : 'en_US'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title || defaultTitle} />
        <meta name="twitter:description" content={description || defaultDescription} />
        <link rel="canonical" href={`https://urenbuddy.nl${location.pathname}`} />
        <html lang={currentLang} />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        {/* Navigation */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-3" data-testid="marketing-logo">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-white">Urenbuddy</span>
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive(link.href)
                        ? 'text-cyan-400 bg-cyan-500/10'
                        : 'text-slate-300 hover:text-white hover:bg-slate-800'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>

              {/* Right side */}
              <div className="flex items-center gap-3">
                {/* Language Switcher */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800">
                      <Globe className="w-4 h-4 mr-2" />
                      {currentLang.toUpperCase()}
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                    <DropdownMenuItem 
                      onClick={() => changeLanguage('nl')} 
                      className={`text-slate-300 hover:text-white hover:bg-slate-700 ${currentLang === 'nl' ? 'bg-slate-700' : ''}`}
                    >
                      🇳🇱 Nederlands
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => changeLanguage('en')} 
                      className={`text-slate-300 hover:text-white hover:bg-slate-700 ${currentLang === 'en' ? 'bg-slate-700' : ''}`}
                    >
                      🇬🇧 English
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Auth buttons */}
                <div className="hidden sm:flex items-center gap-2">
                  <Button asChild variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
                    <Link to="/login" data-testid="nav-login">{t('marketing.nav.login')}</Link>
                  </Button>
                  <Button asChild className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white">
                    <Link to="/register" data-testid="nav-register">{t('marketing.nav.register')}</Link>
                  </Button>
                </div>

                {/* Mobile menu button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden text-slate-300"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden bg-slate-900 border-t border-slate-800">
              <nav className="container mx-auto px-4 py-4 space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium ${
                      isActive(link.href)
                        ? 'text-cyan-400 bg-cyan-500/10'
                        : 'text-slate-300 hover:bg-slate-800'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-4 border-t border-slate-800 space-y-2">
                  <Button asChild variant="outline" className="w-full border-slate-600 text-slate-300">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      {t('marketing.nav.login')}
                    </Link>
                  </Button>
                  <Button asChild className="w-full bg-gradient-to-r from-cyan-500 to-teal-500">
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                      {t('marketing.nav.register')}
                    </Link>
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </header>

        {/* Main Content */}
        <main className="flex-1 pt-16">
          {children}
        </main>

        {/* Footer */}
        <footer className="bg-slate-900 border-t border-slate-800">
          <div className="container mx-auto px-4 py-12">
            <div className="grid md:grid-cols-4 gap-8">
              {/* Brand */}
              <div className="md:col-span-1">
                <Link to="/" className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-xl font-bold text-white">Urenbuddy</span>
                </Link>
                <p className="text-slate-400 text-sm">
                  {t('marketing.footer.tagline')}
                </p>
              </div>

              {/* Product */}
              <div>
                <h4 className="font-semibold text-white mb-4">{t('marketing.footer.product')}</h4>
                <ul className="space-y-2">
                  <li><Link to="/functies" className="text-slate-400 hover:text-cyan-400 text-sm">{t('marketing.nav.features')}</Link></li>
                  <li><Link to="/prijzen" className="text-slate-400 hover:text-cyan-400 text-sm">{t('marketing.nav.pricing')}</Link></li>
                  <li><Link to="/blog" className="text-slate-400 hover:text-cyan-400 text-sm">{t('marketing.nav.blog')}</Link></li>
                </ul>
              </div>

              {/* Company */}
              <div>
                <h4 className="font-semibold text-white mb-4">{t('marketing.footer.company')}</h4>
                <ul className="space-y-2">
                  <li><Link to="/over-ons" className="text-slate-400 hover:text-cyan-400 text-sm">{t('marketing.nav.about')}</Link></li>
                  <li><Link to="/contact" className="text-slate-400 hover:text-cyan-400 text-sm">{t('marketing.nav.contact')}</Link></li>
                </ul>
              </div>

              {/* Legal */}
              <div>
                <h4 className="font-semibold text-white mb-4">{t('marketing.footer.legal')}</h4>
                <ul className="space-y-2">
                  <li><Link to="/privacy" className="text-slate-400 hover:text-cyan-400 text-sm">{t('marketing.footer.privacy')}</Link></li>
                  <li><Link to="/voorwaarden" className="text-slate-400 hover:text-cyan-400 text-sm">{t('marketing.footer.terms')}</Link></li>
                </ul>
              </div>
            </div>

            <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-slate-500 text-sm">
                © {new Date().getFullYear()} Urenbuddy. {t('marketing.footer.rights')}
              </p>
              <div className="flex items-center gap-4 text-slate-500 text-sm">
                <span>Made with ❤️ in Nederland</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
